import { useAuth } from '@clerk/clerk-react';

export const isLocalhost = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

export const getOrgSlugFromAuth = (
  auth: ReturnType<typeof useAuth>,
): string | null => {
  try {
    // Get orgSlug directly from Clerk auth
    return auth.orgSlug || null;
  } catch (error) {
    console.error('Failed to extract org slug:', error);
    return null;
  }
};

export const getOrgFromUrl = (): string | null => {
  if (isLocalhost()) {
    // Development: get from hash fragment (format: #org=slug)
    const hash = window.location.hash.substring(1); // Remove the #
    if (hash.startsWith('org=')) {
      return hash.substring(4); // Remove 'org=' prefix
    }
    return null;
  } else {
    // Production: get from subdomain
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Handle different URL patterns:
    // - <slug>.app.banqora.com (production)
    // - <slug>.banqora.dev (development)
    if (parts.length >= 3) {
      // Check for .app.banqora.com pattern
      if (parts.length >= 4 && parts[1] === 'app' && parts[2] === 'banqora') {
        return parts[0];
      }
      // Check for .banqora.dev pattern
      if (parts[1] === 'banqora') {
        return parts[0];
      }
    }
  }

  return null;
};

export const shouldRedirectForOrg = (
  auth: ReturnType<typeof useAuth>,
): boolean => {
  // Only redirect if user is signed in and auth is loaded
  if (!auth.isSignedIn || !auth.isLoaded) {
    return false;
  }

  const orgSlugFromAuth = getOrgSlugFromAuth(auth);
  const orgFromUrl = getOrgFromUrl();

  // If we can't get org from auth or URL, don't redirect
  if (!orgSlugFromAuth || !orgFromUrl) {
    return false;
  }

  // Only redirect if the orgs don't match
  return orgSlugFromAuth !== orgFromUrl;
};

export const buildRedirectUrl = (orgSlug: string): string => {
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  const baseUrl = process.env.REACT_BASE_URL || 'banqora.dev';

  if (isLocalhost()) {
    // Development: use hash fragment with org= prefix
    return `${window.location.protocol}//${window.location.host}${currentPath}${currentSearch}#org=${orgSlug}`;
  } else {
    return `https://${orgSlug}.${baseUrl}${currentPath}${currentSearch}`;
  }
};

// Central validation function to be called once
export const validateAndRedirectOrg = (
  auth: ReturnType<typeof useAuth>,
): void => {
  if (!auth.isSignedIn || !auth.isLoaded) return;

  if (shouldRedirectForOrg(auth)) {
    const orgSlug = getOrgSlugFromAuth(auth);
    if (orgSlug) {
      const redirectUrl = buildRedirectUrl(orgSlug);
      window.location.href = redirectUrl;
    }
  }
};

// Function to ensure hash is present on localhost during navigation
export const ensureHashOnLocalhost = (
  auth: ReturnType<typeof useAuth>,
): void => {
  if (!isLocalhost() || !auth.isSignedIn || !auth.isLoaded) return;

  const orgSlug = getOrgSlugFromAuth(auth);
  const currentOrgFromUrl = getOrgFromUrl();

  // If we have an org but no hash, or wrong hash, fix it
  if (orgSlug && orgSlug !== currentOrgFromUrl) {
    const newUrl = `${window.location.pathname}${window.location.search}#org=${orgSlug}`;
    window.history.replaceState(null, '', newUrl);
  }
};
